// src/components/ChannelPointsSettings.js
//
// Neuer Einstellungsbereich: Twitch Channel Points für Video-Skip.
// Nutzt dieselbe bestehende API-Infrastruktur (axios-Instanz `api`, /api/-Backend)
// wie VideoSettings.js. Ruft ausschließlich die neuen Backend-Endpoints unter
// /channel-points/* auf - keine eigene Skip-Logik, nur Konfiguration der Zuordnung
// Reward-ID -> SKIP_VIDEO (die eigentliche Aktion passiert im Bot-Container).
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Card, Row, Col, Alert, Spinner } from 'react-bootstrap';
import api from '../api/api';

function ChannelPointsSettings({ darkMode, cardBgClass }) {
  const [skipReward, setSkipReward] = useState(null); // aktuelle Zuordnung ({enabled, rewardId, rewardName, cost}) oder null
  const [rewards, setRewards] = useState([]); // Liste vorhandener Twitch-Rewards
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [selectedRewardId, setSelectedRewardId] = useState('');
  const [newRewardName, setNewRewardName] = useState('Aktuelles Video skippen');
  const [newRewardCost, setNewRewardCost] = useState(5000);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchSkipRewardConfig = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const res = await api.get('/channel-points/skip-reward');
      setSkipReward(res.data);
    } catch (err) {
      console.error('Fehler beim Laden der Channel-Points-Konfiguration:', err);
      setError('❌ Konfiguration konnte nicht geladen werden.');
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const fetchRewards = useCallback(async () => {
    setLoadingRewards(true);
    setError('');
    try {
      const res = await api.get('/channel-points/rewards');
      setRewards(res.data.rewards || []);
    } catch (err) {
      const msg = err.response?.data?.error || 'Rewards konnten nicht geladen werden.';
      console.error('Fehler beim Laden der Rewards:', err);
      // Typische Fehlerfälle laut Anforderung sinnvoll anzeigen
      if (err.response?.status === 401) {
        setError(`⚠️ Twitch nicht verbunden oder Access Token ungültig / benötigte Scopes fehlen: ${msg}`);
      } else {
        setError(`❌ ${msg}`);
      }
      setRewards([]);
    } finally {
      setLoadingRewards(false);
    }
  }, []);

  useEffect(() => {
    fetchSkipRewardConfig();
    fetchRewards();
  }, [fetchSkipRewardConfig, fetchRewards]);

  const handleSelectExisting = async () => {
    if (!selectedRewardId) return;
    const reward = rewards.find((r) => r.id === selectedRewardId);
    if (!reward) return;

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/channel-points/skip-reward', {
        rewardId: reward.id,
        rewardName: reward.title,
        cost: reward.cost,
      });
      setSuccess('✅ Reward für Video-Skip verknüpft!');
      await fetchSkipRewardConfig();
    } catch (err) {
      setError(`❌ Fehler beim Speichern: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateReward = async () => {
    if (!newRewardName.trim() || !newRewardCost) return;

    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post('/channel-points/rewards', {
        name: newRewardName.trim(),
        cost: Number(newRewardCost),
      });
      setSuccess(`✅ Reward "${res.data.reward.title}" erstellt und als Skip-Reward aktiviert!`);
      // UI ohne Seitenreload aktualisieren: neue Reward-Liste laden + Konfiguration neu laden
      await Promise.all([fetchRewards(), fetchSkipRewardConfig()]);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      if (err.response?.status === 401) {
        setError(`⚠️ Twitch nicht verbunden / Token ungültig / Scopes fehlen: ${msg}`);
      } else {
        setError(`❌ Reward konnte nicht erstellt werden: ${msg}`);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveLink = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.delete('/channel-points/skip-reward');
      setSuccess('✅ Verknüpfung entfernt (Twitch-Reward bleibt bestehen).');
      await fetchSkipRewardConfig();
    } catch (err) {
      setError(`❌ Fehler beim Entfernen: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const isActive = skipReward?.enabled && skipReward?.rewardId;

  return (
    <Card className={`p-4 shadow-sm mb-5 ${darkMode ? cardBgClass : ''}`}>
      <h4 className="mb-3">🎟️ Twitch Channel Points</h4>
      <h6 className={darkMode ? 'text-light mb-3' : 'text-muted mb-3'}>Aktion: Video überspringen</h6>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      {/* Aktueller Status */}
      <Card className={`p-3 mb-3 ${darkMode ? 'bg-dark border-secondary' : 'bg-light'}`}>
        {loadingConfig ? (
          <Spinner animation="border" size="sm" />
        ) : isActive ? (
          <>
            <div><strong>Channel Points für Video-Skip:</strong> ✅ Aktiv</div>
            <div><strong>Reward:</strong> {skipReward.rewardName}</div>
            <div><strong>Kosten:</strong> {Number(skipReward.cost).toLocaleString('de-DE')} Channel Points</div>
            <Button
              variant="outline-danger"
              size="sm"
              className="mt-2"
              onClick={handleRemoveLink}
              disabled={saving}
            >
              {saving ? 'Entferne...' : '🗑️ Verknüpfung entfernen'}
            </Button>
          </>
        ) : (
          <div><strong>Channel Points für Video-Skip:</strong> ❌ Inaktiv</div>
        )}
      </Card>

      <hr />

      {/* Variante A: Vorhandenen Reward auswählen */}
      <Form.Group className="mb-3">
        <Form.Label className={darkMode ? 'text-light' : ''}>
          Vorhandenen Channel Point Reward auswählen:
        </Form.Label>
        <Row>
          <Col>
            <Form.Select
              value={selectedRewardId}
              onChange={(e) => setSelectedRewardId(e.target.value)}
              className={darkMode ? cardBgClass : ''}
              disabled={loadingRewards}
            >
              <option value="">-- Reward auswählen --</option>
              {rewards.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.title} – {Number(r.cost).toLocaleString('de-DE')} Punkte
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs="auto">
            <Button variant="secondary" onClick={fetchRewards} disabled={loadingRewards}>
              {loadingRewards ? '⏳' : '🔄'}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              variant="primary"
              onClick={handleSelectExisting}
              disabled={!selectedRewardId || saving}
            >
              {saving ? 'Speichern...' : '✅ Übernehmen'}
            </Button>
          </Col>
        </Row>
      </Form.Group>

      <hr />

      {/* Variante B: Neuen Reward erstellen */}
      <Form.Label className={darkMode ? 'text-light' : ''}>
        Neuen Channel Point Reward erstellen:
      </Form.Label>
      <Row className="mb-2">
        <Col>
          <Form.Control
            type="text"
            placeholder="Reward Name (z. B. Aktuelles Video skippen)"
            value={newRewardName}
            onChange={(e) => setNewRewardName(e.target.value)}
            className={darkMode ? `${cardBgClass} border-light` : ''}
          />
        </Col>
        <Col xs="auto" style={{ minWidth: '160px' }}>
          <Form.Control
            type="number"
            min="1"
            placeholder="Kosten"
            value={newRewardCost}
            onChange={(e) => setNewRewardCost(e.target.value)}
            className={darkMode ? `${cardBgClass} border-light` : ''}
          />
        </Col>
      </Row>
      <Button variant="success" onClick={handleCreateReward} disabled={creating}>
        {creating ? 'Erstelle...' : '➕ Channel Point erstellen'}
      </Button>
    </Card>
  );
}

export default ChannelPointsSettings;
